"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import FontLoader from "@/components/font-loader";
import { useSiteSettings } from "@/components/site-settings-provider";
import { resolvePublicAssetUrl, resolvePublicPageUrl } from "@/lib/api";

type HeroAction = {
  text?: string;
  url?: string;
};

type HeroHighlight = {
  text?: string;
};

type HeroSpotlightCard = {
  title?: string;
  text?: string;
  gradient?: string;
};

type HeroPulseMetric = {
  label?: string;
  value?: string;
  delta?: string;
};

type HeroSlide = {
  id?: string;
  badge?: string;
  heading?: string;
  highlight?: string;
  trailingHeading?: string;
  description?: string;
  primaryCta?: HeroAction;
  secondaryCta?: HeroAction;
  highlights?: HeroHighlight[];
  spotlightCards?: HeroSpotlightCard[];
  pulseMetrics?: HeroPulseMetric[];
  pulseTitle?: string;
  pulseStatus?: string;
  heroVisual?: {
    type?: string;
    src?: string;
    poster?: string;
    alt?: string;
  };
};

const fallbackSpotlightCards: HeroSpotlightCard[] = [
  { title: "Popup Widgets", text: "Trigger timed and behavior-based popups that convert high-intent visitors.", gradient: "from-violet-500 to-fuchsia-500" },
  { title: "Social Proof", text: "Show live activity and trust cues to increase confidence at decision points.", gradient: "from-sky-500 to-cyan-500" },
  { title: "Lead Collectors", text: "Capture emails with contextual forms and route leads to your CRM instantly.", gradient: "from-emerald-500 to-teal-500" },
  { title: "Conversion Analytics", text: "Track CTR, captures, and uplift across every on-site engagement flow.", gradient: "from-amber-500 to-orange-500" },
];

function asString(value: unknown, fallback = "") {
  return typeof value === "string" ? value : fallback;
}

function toHighlights(value: unknown) {
  return Array.isArray(value)
    ? value.filter((item): item is HeroHighlight => Boolean(item && typeof item === "object"))
    : [];
}

function toSpotlightCards(value: unknown) {
  return Array.isArray(value)
    ? value.filter((item): item is HeroSpotlightCard => Boolean(item && typeof item === "object"))
    : [];
}

function toPulseMetrics(value: unknown) {
  return Array.isArray(value)
    ? value.filter((item): item is HeroPulseMetric => Boolean(item && typeof item === "object"))
    : [];
}

export function HomeHeroCarousel({
  fallbackContent,
  slides,
  autoRotateMs = 7000,
}: {
  fallbackContent: Record<string, unknown>;
  slides: HeroSlide[];
  autoRotateMs?: number;
}) {
  const normalizedSlides = useMemo(() => {
    if (slides.length > 0) {
      return slides;
    }

    return [{
      id: "default-hero-slide",
      badge: asString(fallbackContent.badge),
      heading: asString(fallbackContent.heading),
      highlight: asString(fallbackContent.highlight),
      trailingHeading: asString(fallbackContent.trailingHeading),
      description: asString(fallbackContent.description),
      primaryCta: (fallbackContent.primaryCta as HeroAction | undefined) || { text: "Get Free Consultation", url: "/contact-us" },
      secondaryCta: (fallbackContent.secondaryCta as HeroAction | undefined) || { text: "View Case Studies", url: "/resources/case-studies" },
      highlights: toHighlights(fallbackContent.highlights),
      spotlightCards: toSpotlightCards(fallbackContent.spotlightCards).length > 0
        ? toSpotlightCards(fallbackContent.spotlightCards).slice(0, 4)
        : fallbackSpotlightCards,
    }];
  }, [fallbackContent, slides]);

  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (normalizedSlides.length <= 1 || autoRotateMs <= 0) {
      return;
    }

    const timer = window.setInterval(() => {
      setActiveIndex((current) => (current + 1) % normalizedSlides.length);
    }, autoRotateMs);

    return () => window.clearInterval(timer);
  }, [autoRotateMs, normalizedSlides.length]);

  useEffect(() => {
    if (activeIndex >= normalizedSlides.length) {
      setActiveIndex(0);
    }
  }, [activeIndex, normalizedSlides.length]);

  const activeSlide = normalizedSlides[activeIndex];
  const slideTypography = (activeSlide as any).typography || (fallbackContent && (fallbackContent.typography as Record<string, unknown>)) || {};
  const { settings } = useSiteSettings();
  const siteTypography = (settings as any)?.typography || {};
  const headingFont = typeof slideTypography.headingFontFamily === "string"
    ? slideTypography.headingFontFamily
    : (siteTypography && (siteTypography.headingFontFamily || siteTypography.headingFont));
  const headingGradientColors = Array.isArray((slideTypography as any)?.headingGradientColors)
    ? ((slideTypography as any).headingGradientColors as string[])
    : Array.isArray(siteTypography?.headingGradientColors)
    ? (siteTypography.headingGradientColors as string[])
    : undefined;
  const headingGradient = headingGradientColors ? `linear-gradient(120deg, ${headingGradientColors.join(", ")})` : undefined;
  const useRainbow = ((slideTypography && typeof (slideTypography as any).headingStyle === "string" && ((slideTypography as any).headingStyle as string).toLowerCase() === "rainbow") ||
    (siteTypography && typeof siteTypography.headingStyle === "string" && (siteTypography.headingStyle as string).toLowerCase() === "rainbow"));
  const highlights = toHighlights(activeSlide.highlights).length > 0 ? toHighlights(activeSlide.highlights) : toHighlights(fallbackContent.highlights);
  const spotlightCards = toSpotlightCards(activeSlide.spotlightCards).length > 0 ? toSpotlightCards(activeSlide.spotlightCards).slice(0, 4) : fallbackSpotlightCards;
  const heroVisual = (activeSlide.heroVisual && typeof activeSlide.heroVisual === "object") ? activeSlide.heroVisual : undefined;
  const hasMediaVisual = heroVisual && ["image", "video", "media"].includes(asString(heroVisual.type).toLowerCase()) && asString(heroVisual.src);
  const fallbackPulseMetrics: HeroPulseMetric[] = [
    { label: "Live Widgets", value: "128", delta: "+22%" },
    { label: "Popup Impressions", value: "12.4M", delta: "+34%" },
    { label: "Leads Captured", value: "870K", delta: "+41%" },
  ];
  const pulseMetrics = toPulseMetrics(activeSlide.pulseMetrics).length > 0
    ? toPulseMetrics(activeSlide.pulseMetrics)
    : toPulseMetrics(fallbackContent.pulseMetrics).length > 0
      ? toPulseMetrics(fallbackContent.pulseMetrics)
      : fallbackPulseMetrics;
  const pulseTitle = asString(activeSlide.pulseTitle, asString(fallbackContent.pulseTitle, "Live Engagement Pulse"));
  const pulseStatus = asString(activeSlide.pulseStatus, asString(fallbackContent.pulseStatus, "Live"));
  const featureTicker = spotlightCards.map((item) => asString(item.title)).filter(Boolean);

  return (
    <section className="relative overflow-hidden bg-background py-14 lg:py-20">
      {headingFont ? <FontLoader font={headingFont} /> : null}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(59,130,246,0.16),transparent_35%),radial-gradient(circle_at_bottom_right,rgba(139,92,246,0.12),transparent_30%)]" />
      <div className="relative mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-[1.1fr_0.9fr] lg:px-8">
        <div className="max-w-3xl">
          <span className="inline-flex rounded-full border border-sky-200 bg-sky-50 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.24em] text-sky-700 dark:border-sky-900 dark:bg-sky-950/50 dark:text-sky-300">
            {asString(activeSlide.badge)}
          </span>
          <h1 className="mt-6 text-2xl font-bold tracking-tight leading-tight sm:text-3xl md:text-3xl lg:text-4xl xl:text-5xl" style={{
            color: typeof slideTypography.headingColor === "string" ? slideTypography.headingColor : undefined,
            textAlign: typeof slideTypography.align === "string" ? (slideTypography.align as any) : undefined,
            fontFamily: headingFont as any,
          }}>
            {asString(activeSlide.heading)}{' '}
            {asString(activeSlide.highlight) && (
              <span className={useRainbow ? "rainbow-text" : "gradient-text"} style={headingGradient ? ({ ["--animated-spectrum" as any]: headingGradient } as any) : undefined}>
                {asString(activeSlide.highlight)}
              </span>
            )}{' '}
            {asString(activeSlide.trailingHeading)}
          </h1>
          <p className="mt-6 max-w-2xl leading-8 lg:text-xl" style={{ color: typeof slideTypography.descriptionColor === "string" ? slideTypography.descriptionColor : undefined, textAlign: typeof slideTypography.align === "string" ? (slideTypography.align as any) : undefined }}>{asString(activeSlide.description)}</p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link href={resolvePublicPageUrl(asString(activeSlide.primaryCta?.url, "/contact-us"))}>
              <Button variant="gradient" size="lg">{asString(activeSlide.primaryCta?.text, "Get Free Consultation")}</Button>
            </Link>
            <Link href={resolvePublicPageUrl(asString(activeSlide.secondaryCta?.url, "/resources/case-studies"))}>
              <Button variant="outline" size="lg">{asString(activeSlide.secondaryCta?.text, "View Case Studies")}</Button>
            </Link>
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            {highlights.map((item, index) => (
              <div key={`${asString(item.text)}-${index}`} className="inline-flex items-center gap-2 rounded-full bg-muted px-3 py-2 text-sm text-muted-foreground">
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                <span>{asString(item.text)}</span>
              </div>
            ))}
          </div>
          {normalizedSlides.length > 1 ? (
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <div className="inline-flex items-center gap-2 rounded-full border border-border bg-background/80 px-3 py-2 text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">
                <span>{String(activeIndex + 1).padStart(2, "0")}</span>
                <span>/</span>
                <span>{String(normalizedSlides.length).padStart(2, "0")}</span>
              </div>
              <div className="flex items-center gap-2">
                {normalizedSlides.map((slide, index) => (
                  <button
                    key={slide.id || `hero-slide-${index}`}
                    type="button"
                    aria-label={`Show hero slide ${index + 1}`}
                    onClick={() => setActiveIndex(index)}
                    className={`h-2.5 rounded-full transition-all ${index === activeIndex ? "w-8 bg-primary" : "w-2.5 bg-border hover:bg-muted-foreground/40"}`}
                  />
                ))}
              </div>
            </div>
          ) : null}
        </div>

        <div className="self-center">
          <div className="relative overflow-hidden rounded-[30px] bg-slate-950 p-5 text-white shadow-[0_40px_120px_rgba(2,6,23,0.32)]">
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_10%_20%,rgba(14,165,233,0.28),transparent_45%),radial-gradient(circle_at_90%_0%,rgba(217,70,239,0.24),transparent_38%),radial-gradient(circle_at_70%_100%,rgba(16,185,129,0.22),transparent_42%)]" />
            <motion.div
              className="pointer-events-none absolute -left-24 top-0 h-48 w-48 rounded-full bg-cyan-400/20 blur-3xl"
              animate={{ x: [0, 14, 0], y: [0, 8, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.div
              className="pointer-events-none absolute -right-16 bottom-0 h-52 w-52 rounded-full bg-fuchsia-500/20 blur-3xl"
              animate={{ x: [0, -16, 0], y: [0, -10, 0] }}
              transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
            />

            <div className="relative grid gap-4">
              {hasMediaVisual ? (
                <div className="overflow-hidden rounded-2xl border border-white/15 bg-black/30 p-2">
                  {asString(heroVisual?.type).toLowerCase() === "video" ? (
                    <video
                      className="h-auto w-full rounded-xl object-cover"
                      autoPlay
                      muted
                      loop
                      playsInline
                      poster={resolvePublicAssetUrl(asString(heroVisual?.poster))}
                      src={resolvePublicAssetUrl(asString(heroVisual?.src))}
                    />
                  ) : (
                    <img
                      src={resolvePublicAssetUrl(asString(heroVisual?.src))}
                      alt={asString(heroVisual?.alt, asString(activeSlide.heading, "Hero visual"))}
                      className="h-auto w-full rounded-xl object-cover"
                    />
                  )}
                </div>
              ) : null}

              <div className="rounded-2xl border border-white/15 bg-white/5 p-4 backdrop-blur-sm">
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-[0.16em] text-white/70">{pulseTitle}</span>
                  <span className="rounded-full border border-emerald-300/40 bg-emerald-400/15 px-2 py-0.5 text-[10px] font-semibold text-emerald-200">{pulseStatus}</span>
                </div>
                <div className="grid gap-3 sm:grid-cols-3">
                  {pulseMetrics.map((metric, index) => (
                    <motion.div
                      key={metric.label}
                      className="rounded-xl border border-white/15 bg-black/20 p-3"
                      animate={{ y: [0, -3, 0] }}
                      transition={{ duration: 2.3 + index * 0.2, repeat: Infinity, ease: "easeInOut" }}
                    >
                      <p className="text-[11px] uppercase tracking-[0.14em] text-white/60">{metric.label}</p>
                      <p className="mt-1 text-lg font-semibold text-white">{metric.value}</p>
                      <p className="text-xs font-medium text-emerald-200">{metric.delta}</p>
                    </motion.div>
                  ))}
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                {spotlightCards.map((item, index) => (
                  <motion.div
                    key={`${asString(item.title)}-${index}`}
                    className="rounded-2xl border border-white/15 bg-white/10 p-4 backdrop-blur-sm"
                    animate={{ y: [0, -3, 0] }}
                    transition={{ duration: 2.4 + index * 0.16, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <div className={`mb-3 h-1.5 w-14 rounded-full bg-gradient-to-r ${asString(item.gradient, "from-sky-500 to-cyan-500")}`} />
                    <h3 className="text-sm font-semibold text-white">{asString(item.title)}</h3>
                    <p className="mt-1 text-xs leading-6 text-white/80">{asString(item.text)}</p>
                  </motion.div>
                ))}
              </div>

              {featureTicker.length > 0 ? (
                <div className="overflow-hidden rounded-2xl border border-white/15 bg-black/20 py-2">
                  <motion.div
                    className="flex w-max gap-2 px-3"
                    animate={{ x: [0, -220] }}
                    transition={{ duration: 9, repeat: Infinity, ease: "linear" }}
                  >
                    {[...featureTicker, ...featureTicker].map((item, idx) => (
                      <span key={`${item}-${idx}`} className="rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-white/80">
                        {item}
                      </span>
                    ))}
                  </motion.div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}